// Connection Status Store — lightweight singleton pub/sub
// No React, no Context, no Redux.
// Tracks whether the backend is reachable based on consecutive API failures.
// Includes active health polling with exponential backoff + jitter when disconnected.

import { config } from '../config';

const BACKOFF_BASE_MS = 2000;
const BACKOFF_CAP_MS = 15000;
const JITTER_MAX_MS = 1000;
const HEALTH_POLL_INTERVAL_MS = 30000;
const HEALTH_FAIL_THRESHOLD = 3;

const state = {
  reachable: true,
  since: Date.now(),
  reconnecting: false,
  nextRetryAt: null,
};

const subscribers = new Set();
const reconnectCallbacks = new Set();

let pollTimer = null;
let currentBackoffMs = BACKOFF_BASE_MS;
let probeController = null;
let healthPollTimer = null;
let consecutiveHealthFails = 0;

function notify() {
  const snapshot = {
    reachable: state.reachable,
    since: state.since,
    reconnecting: state.reconnecting,
    nextRetryAt: state.nextRetryAt,
  };
  subscribers.forEach((cb) => {
    try {
      cb(snapshot);
    } catch {}
  });
}

function fireReconnectCallbacks() {
  reconnectCallbacks.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
}

function stopPollLoop() {
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  if (probeController) {
    probeController.abort();
    probeController = null;
  }
  state.reconnecting = false;
  state.nextRetryAt = null;
}

async function probe() {
  if (state.reachable) {
    stopPollLoop();
    return;
  }

  if (probeController) {
    probeController.abort();
  }
  probeController = new AbortController();

  state.reconnecting = true;
  notify();

  const healthUrl = (config.apiUrl || window.location.origin) + '/health';

  try {
    const resp = await fetch(healthUrl, {
      signal: probeController.signal,
      cache: 'no-store',
    });
    probeController = null;

    if (resp.ok) {
      ConnectionStatusStore.setReachable(true);
    } else {
      scheduleNextProbe();
    }
  } catch (err) {
    probeController = null;
    if (err?.name === 'AbortError') return;
    scheduleNextProbe();
  }
}

function scheduleNextProbe() {
  state.reconnecting = false;

  if (state.reachable) {
    stopPollLoop();
    return;
  }

  const jitter = Math.random() * JITTER_MAX_MS;
  const delay = Math.min(currentBackoffMs, BACKOFF_CAP_MS) + jitter;
  currentBackoffMs = Math.min(currentBackoffMs * 2, BACKOFF_CAP_MS);

  state.nextRetryAt = Date.now() + delay;
  notify();

  pollTimer = setTimeout(() => {
    pollTimer = null;
    probe();
  }, delay);
}

function startPollLoop() {
  if (pollTimer !== null) return;
  currentBackoffMs = BACKOFF_BASE_MS;
  scheduleNextProbe();
}

function stopHealthPoller() {
  if (healthPollTimer !== null) {
    clearTimeout(healthPollTimer);
    healthPollTimer = null;
  }
}

async function healthPoll() {
  healthPollTimer = null;
  const healthUrl = (config.apiUrl || window.location.origin) + '/health';
  try {
    const resp = await fetch(healthUrl, { cache: 'no-store' });
    if (resp.ok) {
      consecutiveHealthFails = 0;
      if (!state.reachable) ConnectionStatusStore.setReachable(true);
    } else {
      consecutiveHealthFails += 1;
      if (consecutiveHealthFails >= HEALTH_FAIL_THRESHOLD) {
        ConnectionStatusStore.setReachable(false);
      }
    }
  } catch {
    consecutiveHealthFails += 1;
    if (consecutiveHealthFails >= HEALTH_FAIL_THRESHOLD) {
      ConnectionStatusStore.setReachable(false);
    }
  }
  if (state.reachable) {
    healthPollTimer = setTimeout(healthPoll, HEALTH_POLL_INTERVAL_MS);
  }
}

function startHealthPoller() {
  if (healthPollTimer !== null) return;
  healthPollTimer = setTimeout(healthPoll, HEALTH_POLL_INTERVAL_MS);
}

export const ConnectionStatusStore = {
  getState() {
    return {
      reachable: state.reachable,
      since: state.since,
      reconnecting: state.reconnecting,
      nextRetryAt: state.nextRetryAt,
    };
  },

  setReachable(reachable) {
    if (state.reachable === reachable) return;
    state.reachable = reachable;
    state.since = Date.now();

    if (reachable) {
      consecutiveHealthFails = 0;
      stopPollLoop();
      notify();
      fireReconnectCallbacks();
      startHealthPoller();
    } else {
      stopHealthPoller();
      notify();
      startPollLoop();
    }
  },

  forceReconnectCheck() {
    if (state.reachable) return;
    if (pollTimer !== null) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    state.nextRetryAt = null;
    probe();
  },

  onReconnect(fn) {
    reconnectCallbacks.add(fn);
  },

  offReconnect(fn) {
    reconnectCallbacks.delete(fn);
  },

  subscribe(fn) {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  },
};

startHealthPoller();
